import React from "react";
import SlipMark from "../assets/slip-mark-light.svg";
import SlipMarkDark from "../assets/slip-mark-dark.svg";
import ThemeToggle from "../components/ThemeToggle";
import menu from "../assets/menu.svg";
import { trackEvent } from "../utils/clarity";

// Constants
const LINKS = {
    PLAYGROUND: "https://playground.tryonlabs.ai",
    DOCS: "https://docs.tryonlabs.ai",
    GITHUB: "https://github.com/tryonlabs",
    DISCORD: "https://discord.gg/tryonlabs",
    PRICING: "https://playground.tryonlabs.ai/pricing",
    CREDITS: "https://playground.tryonlabs.ai/credits",
    HOME: "/",
    CONTACT: "mailto:contact@tryonlabs.ai",
    SUPPORT: "mailto:support@tryonlabs.ai",
    PARTNERSHIP: "mailto:contact@tryonlabs.ai?subject=Partnership",
    ENTERPRISE: "mailto:contact@tryonlabs.ai?subject=Enterprise Pricing Inquiry"
};

const SEO = {
    TITLE: "TryOn AI - Fashion AI APIs for Developers",
    DESCRIPTION: "Build fashion apps without AI complexity. APIs for virtual try-on, model swap, model generation, image and video generation, and studio edits. Test each call in the playground, then integrate.",
    KEYWORDS: "fashion AI, virtual try-on, AI APIs, fashion tech, developer APIs, machine learning, computer vision"
};

// Analytics tracking function
const trackCTAClick = (section, action) => {
    // Clarity event tracking
    trackEvent('CTA Click', {
        section,
        action,
        category: 'engagement'
    });
};

// Loading state hook
const useLoadingState = () => {
    const [isLoading, setIsLoading] = React.useState(false);
    
    const handleExternalLink = (url, section, action) => {
        setIsLoading(true);
        trackCTAClick(section, action);
        
        // Simulate loading time for better UX
        setTimeout(() => {
            window.open(url, '_blank', 'noopener,noreferrer');
            setIsLoading(false);
        }, 500);
    };
    
    return { isLoading, handleExternalLink };
};

const Navbar = () => {
    return (
        <nav role="navigation" aria-label="Main navigation" className="fixed top-0 z-40 bg-ground h-auto w-screen sm:w-full border-b border-hairline">
            <div className="mx-auto max-w-7xl flex items-center justify-between p-2 sm:px-4">
                <div className="flex items-center mr-6">
                    <div className="flex items-center cursor-pointer" onClick={() => window.location.href = LINKS.HOME}>
                        <img className="nav-menu w-6 h-6 object-contain cursor-pointer mr-2 md:hidden" src={menu} alt="Menu" loading="lazy" />
                        <img className="mark-on-light w-8 h-8 shrink-0 object-contain" src={SlipMark} alt="" loading="lazy" />
                        <img className="mark-on-dark w-8 h-8 shrink-0 object-contain" src={SlipMarkDark} alt="" loading="lazy" />
                        <span className="hidden md:block ml-3 text-xl font-bold text-ink">TryOn AI</span>
                    </div>
                </div>

                <div className="flex items-center justify-center whitespace-nowrap md:w-max pointer-events-auto gap-0.5 sm:gap-2 md:gap-3">
                    <ThemeToggle />
                    <a target="_blank" href={LINKS.GITHUB} rel="noopener noreferrer" className="hidden md:flex items-center justify-center w-10 h-8 text-muted hover:text-ink transition-colors" aria-label="GitHub Repository">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                    </a>
                    <a href={LINKS.PLAYGROUND} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('navbar', 'Try Playground')}>
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            Try Our Playground
                        </button>
                    </a>
                    <a target="_blank" href={LINKS.DOCS} rel="noopener noreferrer" onClick={() => trackCTAClick('navbar', 'Docs')}>
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            Docs
                        </button>
                    </a>
                    <a target="_blank" href={LINKS.PRICING} rel="noopener noreferrer" onClick={() => trackCTAClick('navbar', 'Pricing')}>
                        <button className="hidden sm:flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            Pricing
                        </button>
                    </a>
                    <a href={LINKS.CREDITS} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('navbar', 'Get API Access')}>
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-white bg-action rounded-full hover:bg-action-hover transition-colors shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]">
                            Get API Access
                        </button>
                    </a>
                </div>
            </div>
        </nav>
    );
};

const HeroSection = () => {
    const { isLoading, handleExternalLink } = useLoadingState();
    
    return (
        <section aria-labelledby="hero-heading" className="w-full py-20 md:py-28 bg-ground">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center space-y-8">
                    {/* Badge */}
                    <div className="inline-flex items-center px-4 py-2 rounded-full border bg-tray border-hairline">
                        <span className="text-ink text-sm font-medium">🚀 Fashion AI APIs</span>
                    </div>

                    {/* Main Headline */}
                    <div className="space-y-4">
                        <h1 id="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-newsreader font-medium text-ink leading-[1.1]">
                            Build Fashion Apps
                            <span className="block">
                                Without AI Complexity
                            </span>
                        </h1>
                    </div>

                    {/* Subtitle */}
                    <div className="space-y-6 max-w-3xl mx-auto">
                        <p className="text-lg sm:text-xl text-muted leading-relaxed">
                            Build virtual try-on experiences, create and swap fashion models, generate images and short videos, and finish pictures with studio edits.
                        </p>
                        <p className="text-lg sm:text-xl text-ink font-semibold leading-relaxed">
                            Our APIs handle the complexity so you can focus on building amazing fashion apps.
                        </p>
                    </div>
                    
                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                        <button 
                            onClick={() => handleExternalLink(LINKS.PLAYGROUND, 'hero_cta', 'Try Playground')}
                            disabled={isLoading}
                            className="group flex items-center justify-center px-8 py-4 font-semibold text-white bg-action rounded-full hover:bg-action-hover transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <span className="mr-2">Try Playground</span>
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </>
                            )}
                        </button>
                        <a href={LINKS.CREDITS} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('hero_cta', 'Get API Access')}>
                            <button className="flex items-center justify-center px-8 py-4 font-medium text-coral border border-coral-line rounded-full hover:bg-tray transition-all duration-300 bg-sheet">
                                Get API Access
                            </button>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

// How Our Infrastructure Helps Fashion Startups Section - Modern Design
const HowWeHelpSection = () => (
    <section aria-labelledby="how-we-help-heading" className="w-full py-20 md:py-28 bg-sheet">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-20">
                <h2 id="how-we-help-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-ink mb-6 leading-tight">
                    Built for fashion developers
                </h2>
                <p className="text-lg sm:text-xl text-muted max-w-3xl mx-auto leading-relaxed">
                    Skip the AI complexity. Focus on what matters most: building exceptional fashion applications.
                </p>
            </div>

            {/* Main Content - Modern Layout */}
            <div className="space-y-16">
                {/* Problem vs Solution - Enhanced Visibility */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Problem Statement */}
                    <div className="bg-sheet rounded-2xl p-8 border border-hairline text-center lg:text-left">
                        <div className="inline-block px-4 py-2 bg-tray rounded-full border mb-6 border-hairline">
                            <span className="text-ink text-sm font-medium">The Problem</span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-ink mb-6">
                            Building fashion AI from scratch is complex
                        </h3>
                        <p className="text-muted leading-relaxed">
                            Developers spend months researching models and setting up ML infrastructure. 
                            Building reliable virtual try-on requires deep AI expertise most teams lack.
                        </p>
                    </div>

                    {/* Solution Statement */}
                    <div className="bg-sheet rounded-2xl p-8 border text-center lg:text-left relative overflow-hidden border-hairline">
                        {/* Subtle gradient background */}
                        <div className="absolute inset-0"></div>
                        <div className="relative">
                            <div className="inline-block px-4 py-2 bg-tray rounded-full border mb-6 border-hairline">
                                <span className="text-ink text-sm font-medium">Our Solution</span>
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold text-ink mb-6">
                                Production-ready APIs for fashion apps
                            </h3>
                            <p className="text-muted leading-relaxed">
                                Integrate fashion AI in days, not months. Our APIs handle ML complexity so you can focus on user experiences.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Benefits Grid - Modern 3-Column Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    <div className="flex items-start space-x-4">
                        <div className="w-2 h-2 bg-ink rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                            <h4 className="text-lg font-semibold text-ink mb-2">Skip AI complexity</h4>
                            <p className="text-muted text-sm leading-relaxed">
                                Integrate advanced fashion AI with simple REST APIs. No ML expertise required.
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                        <div className="w-2 h-2 bg-ink rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                            <h4 className="text-lg font-semibold text-ink mb-2">Built for developers</h4>
                            <p className="text-muted text-sm leading-relaxed">
                                Clean documentation, SDKs, and code examples. Get your first API call working in minutes.
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                        <div className="w-2 h-2 bg-ink rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                            <h4 className="text-lg font-semibold text-ink mb-2">Developer-friendly pricing</h4>
                            <p className="text-muted text-sm leading-relaxed">
                                Pay per API call. No upfront costs or infrastructure management. Scale as your app grows.
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                        <div className="w-2 h-2 bg-ink rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                            <h4 className="text-lg font-semibold text-ink mb-2">Focus on your app</h4>
                            <p className="text-muted text-sm leading-relaxed">
                                Build amazing user experiences and core features, not AI infrastructure and model training.
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                        <div className="w-2 h-2 bg-ink rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                            <h4 className="text-lg font-semibold text-ink mb-2">Quick integration</h4>
                            <p className="text-muted text-sm leading-relaxed">
                                RESTful APIs, JavaScript/Python SDKs, and comprehensive docs. Integrate in hours, not weeks.
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                        <div className="w-2 h-2 bg-ink rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                            <h4 className="text-lg font-semibold text-ink mb-2">Scales with your app</h4>
                            <p className="text-muted text-sm leading-relaxed">
                                Pay per API call with credits, so you can start with a prototype and grow usage as your app does.
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* CTA */}
                <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-ink">
                        <span className="text-sm font-medium">Ready to start building?</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const OpenSourceSection = () => (
    <section aria-labelledby="open-source-heading" className="w-full py-20 md:py-28 bg-ground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-20">
                <h2 id="open-source-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-ink mb-6 leading-tight">
                    Developer Community
                </h2>
                <p className="text-lg sm:text-xl text-muted max-w-3xl mx-auto leading-relaxed">
                    Built by developers, for developers. Open source tools and a thriving community.
                </p>
            </div>

            {/* Main Content */}
            <div className="space-y-16">
                {/* Community Features */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-sheet rounded-2xl p-8 border border-hairline">
                        <h3 className="text-2xl font-bold text-ink mb-4">Open Source SDKs</h3>
                        <p className="text-muted mb-6 leading-relaxed">
                        Open source SDKs with full customization freedom. Fork, extend, and contribute to our growing ecosystem.
                        </p>
                        <a href={LINKS.GITHUB} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-ink hover:text-coral transition-colors" onClick={() => trackCTAClick('open_source', 'View on GitHub')}>
                            <span className="mr-2">View on GitHub</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                    </a>
                </div>

                    <div className="bg-sheet rounded-2xl p-8 border border-hairline">
                        <h3 className="text-2xl font-bold text-ink mb-4">Developer Community</h3>
                        <p className="text-muted mb-6 leading-relaxed">
                            Join our Discord community of fashion tech developers. Get help, share projects, and collaborate.
                        </p>
                        <a href={LINKS.DISCORD} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-ink hover:text-coral transition-colors" onClick={() => trackCTAClick('open_source', 'Join Discord')}>
                            <span className="mr-2">Join Discord</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                    </a>
                </div>
            </div>

                {/* CTA */}
            <div className="text-center">
                    <a href={LINKS.GITHUB} className="inline-flex items-center justify-center" target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('open_source', 'Star on GitHub')}>
                        <button className="group flex items-center justify-center px-8 py-4 font-semibold text-coral bg-sheet border border-coral-line rounded-full hover:bg-tray transition-all duration-300">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                            </svg>
                            <span className="mr-2">Star on GitHub</span>
                            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </a>
                </div>
            </div>
        </div>
    </section>
);

const DeveloperQuickstartSection = () => (
    <section aria-labelledby="quickstart-heading" className="w-full py-20 md:py-28 bg-sheet">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-20">
                <h2 id="quickstart-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-ink mb-6 leading-tight">
                    Start building today
                </h2>
                <p className="text-lg sm:text-xl text-muted max-w-3xl mx-auto leading-relaxed">
                    Test our APIs in the live playground, then integrate with simple REST endpoints
                </p>
            </div>

            {/* Main Content */}
            <div className="space-y-16">
                {/* Quick Code Example */}
                <div className="bg-sheet rounded-2xl p-8 md:p-12 border border-hairline">
                <div className="text-center mb-12">
                        <div className="inline-flex items-center px-4 py-2 bg-tray rounded-full border mb-8 border-hairline">
                        <span className="text-ink text-sm font-medium">Quickstart</span>
                    </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-ink mb-4">
                            Make your first call
                    </h3>
                        <p className="text-muted max-w-2xl mx-auto">
                            Get an access token and run your first virtual try-on
                        </p>
                    </div>

                    <div className="space-y-8">
                        {/* Installation */}
                        <div className="bg-ground rounded-xl p-6 border border-hairline">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                </div>
                                <span className="text-muted text-sm">Terminal</span>
                            </div>
                            <pre className="text-muted text-sm overflow-x-auto">
                                <code>{`export TOKEN="<your access token>"`}</code>
                            </pre>
                </div>

                {/* Code Example */}
                        <div className="bg-ground rounded-xl p-6 border border-hairline">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                </div>
                                <span className="text-muted text-sm">curl</span>
                            </div>
                            <pre className="text-muted text-sm overflow-x-auto">
                                <code>{`curl -X POST https://api.tryonlabs.ai/api/v1/vton/generate/ \\
  -H "Authorization: Bearer $TOKEN" \\
  -F "person_image=@person.jpg" \\
  -F "garment_image=@shirt.jpg" \\
  -F "resolution=2k" \\
  -F "generation_mode=quality"`}</code>
                            </pre>
                        </div>
                    </div>
                </div>

                {/* Integration Steps */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-tray rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <span className="text-2xl font-bold text-ink">1</span>
                        </div>
                        <h3 className="text-xl font-bold text-ink mb-4">Get a token</h3>
                        <p className="text-muted leading-relaxed">
                            Create an account and request an access token
                        </p>
                    </div>

                    <div className="text-center">
                        <div className="w-16 h-16 bg-tray rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <span className="text-2xl font-bold text-ink">2</span>
                        </div>
                        <h3 className="text-xl font-bold text-ink mb-4">Send images</h3>
                        <p className="text-muted leading-relaxed">
                            Send a person photo and a garment photo
                        </p>
                    </div>

                    <div className="text-center">
                        <div className="w-16 h-16 bg-tray rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <span className="text-2xl font-bold text-ink">3</span>
                        </div>
                        <h3 className="text-xl font-bold text-ink mb-4">Download the result</h3>
                        <p className="text-muted leading-relaxed">
                            The response includes an id; download the finished image with it
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const ROUTES = [
    { name: 'Virtual try-on', path: 'POST /vton/generate/', desc: 'A person photo and a garment photo in, a finished look out.', doc: 'api/vton-generate-create' },
    { name: 'Model swap', path: 'POST /model/swap/', desc: 'Keep the outfit and change the person wearing it.', doc: 'api/model-swap-create' },
    { name: 'Model create', path: 'POST /model/create/', desc: 'Create a fashion model from a description.', doc: 'api/model-create-create' },
    { name: 'Product on model', path: 'POST /product/on-model/', desc: 'Turn a flat or ghost-mannequin product photo into a worn shot.', doc: 'api/product-on-model-create' },
    { name: 'Image generate', path: 'POST /image/generate/', desc: 'Make a picture from a prompt, or edit an existing image.', doc: 'api/image-generate-create' },
    { name: 'Video generate', path: 'POST /video/generate/', desc: 'Start a short clip, poll its status, then download the MP4.', doc: 'api/video-generate-create' },
];

const RoutesSection = () => (
    <section aria-labelledby="routes-heading" className="w-full py-20 md:py-28 bg-ground">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
                <h2 id="routes-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-ink mb-6 leading-tight">
                    What you can call
                </h2>
                <p className="text-lg sm:text-xl text-muted max-w-3xl mx-auto leading-relaxed">
                    REST endpoints under <code>api.tryonlabs.ai/api/v1</code>, authenticated with a bearer token.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {ROUTES.map((r) => (
                    <a key={r.name} href={`${LINKS.DOCS}/${r.doc}`} target="_blank" rel="noopener noreferrer"
                       onClick={() => trackCTAClick('routes', r.name)}
                       className="block bg-sheet rounded-2xl p-6 border border-hairline hover:bg-tray transition-colors">
                        <h3 className="text-xl font-bold text-ink mb-2">{r.name}</h3>
                        <code className="block text-xs text-muted mb-4">{r.path}</code>
                        <p className="text-muted leading-relaxed">{r.desc}</p>
                    </a>
                ))}
            </div>
            <p className="text-center text-muted mt-10">
                Credit cost depends on resolution and mode. See <a className="text-coral underline" href={LINKS.PRICING} target="_blank" rel="noopener noreferrer">pricing</a>.
            </p>
        </div>
    </section>
);

// Footer Component
const Footer = () => (
    <footer role="contentinfo" className="w-full py-16 bg-ground border-t border-hairline">
        <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8 mb-12">
                {/* Company */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">TryOn AI</h4>
                    <p className="text-muted text-sm leading-relaxed mb-4">
                        Your collection, photographed.<br/> Without the studio.
                    </p>
                    <div className="flex space-x-4">
                        <a href={LINKS.CONTACT} className="text-muted hover:text-coral transition-colors" aria-label="Contact us">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M3 8l9 6 9-6v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
                                <path d="M21 6L12 13 3 6h18z"/>
                            </svg>
                        </a>
                    </div>
                </div>

                {/* Playground */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">Playground</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href={LINKS.PLAYGROUND} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Live Playground')}>Live Playground</a></li>
                        <li><a href={LINKS.PRICING} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Pricing')}>Pricing</a></li>
                        <li><a href={LINKS.DOCS} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Documentation')}>Documentation</a></li>
                        <li><a href={LINKS.SUPPORT} className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Support')}>Support</a></li>
                    </ul>
                </div>

                {/* APIs */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">APIs</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href={`${LINKS.DOCS}/quickstart`} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Quickstart')}>Quickstart</a></li>
                        <li><a href={`${LINKS.DOCS}/authentication`} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Authentication')}>Authentication</a></li>
                        <li><a href={`${LINKS.DOCS}/api/vton-generate-create`} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Virtual Try-On')}>Virtual Try-On</a></li>
                        <li><a href={`${LINKS.DOCS}/api/model-swap-create`} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Model Swap')}>Model Swap</a></li>
                        <li><a href={`${LINKS.DOCS}/api/video-generate-create`} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Video')}>Video</a></li>
                    </ul>
                </div>

                {/* Company */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">Company</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href={LINKS.HOME} className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Home')}>Home</a></li>
                        <li><a href={LINKS.GITHUB} target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Open Source')}>Open Source</a></li>
                        <li><a href={LINKS.PARTNERSHIP} className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Partnerships')}>Partnerships</a></li>
                        <li><a href="/contact-us" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Contact Us')}>Contact Us</a></li>
                        <li><a href="/terms-and-conditions" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Terms & Conditions')}>Terms & Conditions</a></li>
                        <li><a href="/cancellation-and-refund-policy" className="text-muted hover:text-coral transition-colors" onClick={() => trackCTAClick('footer', 'Cancellation & Refund Policy')}>Cancellation & Refund Policy</a></li>
                    </ul>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="pt-8 border-t border-hairline flex flex-col md:flex-row justify-between items-center">
                <p className="text-muted text-sm">
                    &copy; {new Date().getFullYear()} TryOn AI. All rights reserved.
                </p>
            </div>
        </div>
    </footer>
);

const PricingSection = () => (
    <section aria-labelledby="pricing-heading" className="w-full py-20 md:py-28 bg-ground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-20">
                <h2 id="pricing-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-ink mb-6 leading-tight">
                    Flexible pricing options
                </h2>
                <p className="text-lg sm:text-xl text-muted max-w-3xl mx-auto leading-relaxed">
                    Choose the plan that fits your needs. No upfront costs, no hidden fees. Scale as your app grows.
                </p>
            </div>

            {/* Pricing Cards */}
            <div className="flex justify-center">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-4xl w-full">
                    {/* Pay-per-API Plan */}
                    <div className="bg-sheet rounded-2xl p-8 border relative border-hairline">
                        <div className="text-center mb-8">
                            <h3 className="text-2xl font-bold text-ink mb-2">Pay-per-API</h3>
                            <div className="text-4xl font-bold text-ink mb-2">Pay As You Go</div>
                            <p className="text-muted">Flexible pricing model</p>
                        </div>
                        <ul className="space-y-4 mb-8">
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Pay only for what you use
                            </li>
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                All APIs included
                            </li>
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Developer support
                            </li>
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                No monthly commitments
                            </li>
                        </ul>
                        <a href={LINKS.PLAYGROUND} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('pricing', 'Start with Free Credits')}>
                            <button className="w-full px-6 py-3 text-white bg-action rounded-full hover:bg-action-hover transition-all duration-300 shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]">
                                Start with Free Credits
                            </button>
                        </a>
                    </div>
                    
                    {/* Enterprise Tier */}
                    <div className="bg-sheet rounded-2xl p-8 border border-hairline">
                        <div className="text-center mb-8">
                            <h3 className="text-2xl font-bold text-ink mb-2">Enterprise</h3>
                            <div className="text-4xl font-bold text-ink mb-2">Custom</div>
                            <p className="text-muted">For high-volume applications</p>
                        </div>
                        <ul className="space-y-4 mb-8">
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Volume discounts
                            </li>
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Dedicated support
                            </li>
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                SLA guarantees
                            </li>
                            <li className="flex items-center text-muted">
                                <svg className="w-5 h-5 text-ink mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Custom integrations
                            </li>
                        </ul>
                    <a href={LINKS.ENTERPRISE} onClick={() => trackCTAClick('pricing', 'Contact Sales')}>
                            <button className="w-full px-6 py-3 text-coral bg-sheet border border-coral-line rounded-full hover:bg-tray transition-all duration-300">
                            Contact Sales
                        </button>
                    </a>
                </div>
                </div>
            </div>
        </div>
    </section>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-ground flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-ink mb-4">Something went wrong</h1>
                        <p className="text-muted mb-6">We're working to fix this issue.</p>
                        <button 
                            onClick={() => window.location.reload()} 
                            className="px-6 py-3 bg-action text-white rounded-full hover:bg-action-hover transition-colors shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default function LandingPage() {
    // SEO Optimization
    React.useEffect(() => {
        // Update document title
        document.title = SEO.TITLE;
        
        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', SEO.DESCRIPTION);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'description';
            meta.content = SEO.DESCRIPTION;
            document.head.appendChild(meta);
        }

        // Update meta keywords
        const metaKeywords = document.querySelector('meta[name="keywords"]');
        if (metaKeywords) {
            metaKeywords.setAttribute('content', SEO.KEYWORDS);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'keywords';
            meta.content = SEO.KEYWORDS;
            document.head.appendChild(meta);
        }

        // Open Graph tags
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
            ogTitle.setAttribute('content', SEO.TITLE);
        } else {
            const meta = document.createElement('meta');
            meta.setAttribute('property', 'og:title');
            meta.content = SEO.TITLE;
            document.head.appendChild(meta);
        }

        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription) {
            ogDescription.setAttribute('content', SEO.DESCRIPTION);
        } else {
            const meta = document.createElement('meta');
            meta.setAttribute('property', 'og:description');
            meta.content = SEO.DESCRIPTION;
            document.head.appendChild(meta);
        }

        const ogImage = document.querySelector('meta[property="og:image"]');
        if (ogImage) {
            ogImage.setAttribute('content', 'https://tryonlabs.ai/og-image.png');
        } else {
            const meta = document.createElement('meta');
            meta.setAttribute('property', 'og:image');
            meta.content = 'https://tryonlabs.ai/og-image.png';
            document.head.appendChild(meta);
        }

        // Twitter Card tags
        const twitterCard = document.querySelector('meta[name="twitter:card"]');
        if (twitterCard) {
            twitterCard.setAttribute('content', 'summary_large_image');
        } else {
            const meta = document.createElement('meta');
            meta.name = 'twitter:card';
            meta.content = 'summary_large_image';
            document.head.appendChild(meta);
        }

        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle) {
            twitterTitle.setAttribute('content', SEO.TITLE);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'twitter:title';
            meta.content = SEO.TITLE;
            document.head.appendChild(meta);
        }

        const twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (twitterDescription) {
            twitterDescription.setAttribute('content', SEO.DESCRIPTION);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'twitter:description';
            meta.content = SEO.DESCRIPTION;
            document.head.appendChild(meta);
        }

        // Add custom CSS animations
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(30px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            .animate-fade-in-up {
                animation: fadeInUp 0.6s ease-out;
            }
            
            @keyframes pulse {
                0%, 100% {
                    opacity: 1;
                }
                50% {
                    opacity: 0.5;
                }
            }
            
            .animate-pulse-slow {
                animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
            }
        `;
        document.head.appendChild(style);

        // Track page view with Clarity
        trackEvent('Page View', {
            page_title: SEO.TITLE,
            page_location: window.location.href
        });

        return () => {
            // Cleanup function
            if (style.parentNode) {
                style.parentNode.removeChild(style);
            }
        };
    }, []);

    return (
        <ErrorBoundary>
            <div className="h-full overflow-hidden w-full bg-ground">
                <Navbar />
                <div className="h-full scrollbar items-center justify-start bg-ground">
                    <HeroSection />
                    <HowWeHelpSection />
                    <RoutesSection />
                    <OpenSourceSection />
                    <DeveloperQuickstartSection />
                    <PricingSection />
                    <Footer />
                </div>
            </div>
        </ErrorBoundary>
    );
}